import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Clock, Bookmark, Share2 } from "lucide-react";
import { CategoryChip, Badge } from "./Chrome";
import { timeAgo, resolveImg } from "../lib/api";
import { useApp } from "../lib/ctx";

export const StoryCard = ({ story, cats, size = "md" }) => {
  const cat = cats?.find(c => c.id === story.category_id);
  const { toggleSave, isSaved } = useApp();
  return (
    <Link to={`/story/${story.slug}`} data-testid={`story-card-${story.slug}`}
      className="group block rounded-2xl overflow-hidden bg-card card-soft hover:translate-y-[-2px] transition-transform">
      <div className="aspect-[16/10] bg-muted overflow-hidden relative">
        {story.cover_image && (
          <img src={resolveImg(story.cover_image)} alt={story.alt_text || story.title} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        )}
        <div className="absolute top-3 left-3 flex gap-1.5">
          {story.is_breaking && <Badge kind="BREAKING" />}
          {story.is_sponsored && <Badge kind="SPONSORED" />}
          {story.correction_note && <Badge kind="UPDATED" />}
        </div>
        <button onClick={(e) => { e.preventDefault(); toggleSave(story); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-background/90 backdrop-blur flex items-center justify-center hover:bg-background"
          data-testid={`save-card-${story.slug}`}>
          <Bookmark className={`w-4 h-4 ${isSaved(story.id) ? "fill-signal text-signal" : ""}`} />
        </button>
      </div>
      <div className="p-4">
        {cat && <CategoryChip category={cat} />}
        <h3 className={`font-display font-extrabold mt-2 clamp-3 ${size === "lg" ? "text-2xl sm:text-3xl" : "text-base sm:text-lg leading-snug"}`}>
          {story.title}
        </h3>
        {story.gist?.[0] && size !== "sm" && (
          <p className="text-sm text-muted-foreground mt-2 clamp-2">{story.gist[0]}</p>
        )}
        <div className="flex items-center gap-2 mt-3 text-[11px] text-muted-foreground font-medium">
          <Clock className="w-3 h-3" />
          <span>{story.read_time || 1} min read</span>
          <span>•</span>
          <span>{timeAgo(story.published_at)}</span>
        </div>
      </div>
    </Link>
  );
};

export const LeadHero = ({ story, cats }) => {
  const cat = cats?.find(c => c.id === story.category_id);
  return (
    <Link to={`/story/${story.slug}`} data-testid="lead-hero"
      className="relative block rounded-3xl overflow-hidden h-[380px] sm:h-[480px] card-soft group">
      {story.cover_image && (
        <img src={resolveImg(story.cover_image)} alt={story.alt_text || story.title}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
      <div className="absolute inset-0 p-6 sm:p-10 flex flex-col justify-end text-white">
        <div className="flex gap-2 mb-3">
          {cat && <CategoryChip category={cat} size="lg" />}
          {story.is_breaking && <Badge kind="BREAKING" />}
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-5xl leading-[1.05] max-w-3xl">{story.title}</h1>
        {story.gist?.[0] && <p className="mt-3 text-white/80 max-w-2xl clamp-2">{story.gist[0]}</p>}
        <div className="mt-4 flex items-center gap-3 text-xs text-white/70">
          <Clock className="w-3 h-3" />
          <span>{story.read_time || 1} min read</span>
          <span>•</span>
          <span>{timeAgo(story.published_at)}</span>
        </div>
      </div>
    </Link>
  );
};

export const SixtySecondsRail = ({ stories, cats }) => {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const nav = useNavigate();
  const timerRef = useRef(null);
  const DUR = 6000;

  const openAt = (i) => { setIdx(i); setOpen(true); };
  const close = () => { setOpen(false); clearTimeout(timerRef.current); };
  const next = React.useCallback(() => setIdx(i => i < stories.length - 1 ? i + 1 : i), [stories.length]);
  const prev = () => setIdx(i => Math.max(0, i - 1));

  useEffect(() => {
    if (!open) return;
    clearTimeout(timerRef.current);
    if (idx < stories.length - 1) timerRef.current = setTimeout(next, DUR);
    else timerRef.current = setTimeout(close, DUR);
    return () => clearTimeout(timerRef.current);
  }, [open, idx, next, stories.length]);

  if (!stories?.length) return null;
  const cur = stories[idx];
  const curCat = cats?.find(c => c.id === cur?.category_id);

  return (
    <>
      <section className="mb-6">
        <div className="flex items-end justify-between px-4 sm:px-6 mb-3">
          <div>
            <h2 className="font-display font-extrabold text-xl sm:text-2xl">Today in 60 Seconds</h2>
            <p className="text-xs text-muted-foreground">Tap a story. Watch it like a reel.</p>
          </div>
          <Link to="/sixty-seconds" className="text-xs font-bold text-signal hover:underline" data-testid="see-all-60s">See all</Link>
        </div>
        <div className="flex gap-3 overflow-x-auto no-scrollbar px-4 sm:px-6 pb-2" data-testid="sixty-rail">
          {stories.map((s, i) => {
            const cat = cats?.find(c => c.id === s.category_id);
            return (
              <button key={s.id} onClick={() => openAt(i)} data-testid={`sixty-story-${s.slug}`}
                className="flex-none w-20 sm:w-24 flex flex-col items-center gap-1.5 group">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full p-[3px]" style={{ background: `conic-gradient(${cat?.color || "#F42A41"}, #F42A41, ${cat?.color || "#F42A41"})` }}>
                  <div className="w-full h-full rounded-full bg-background p-1">
                    <img src={resolveImg(s.cover_image)} alt="" className="w-full h-full rounded-full object-cover" />
                  </div>
                </div>
                <span className="text-[10px] font-semibold text-center clamp-2 leading-tight">{s.title.split(":").pop()}</span>
              </button>
            );
          })}
        </div>
      </section>

      {open && cur && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" data-testid="sixty-modal">
          <button className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center" onClick={close} data-testid="sixty-close"><X className="w-5 h-5" /></button>
          <button className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 text-white hidden sm:flex items-center justify-center" onClick={prev}><ChevronLeft className="w-6 h-6" /></button>
          <button className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 text-white hidden sm:flex items-center justify-center" onClick={next}><ChevronRight className="w-6 h-6" /></button>

          <div className="relative w-full max-w-sm h-full sm:h-[85vh] sm:rounded-3xl overflow-hidden bg-neutral-900" onClick={next}>
            <div className="absolute top-0 inset-x-0 z-10 p-3 flex gap-1">
              {stories.map((_, i) => (
                <div key={i} className="h-1 flex-1 bg-white/30 rounded-full overflow-hidden">
                  {i < idx && <div className="h-full w-full bg-white" />}
                  {i === idx && <div className="h-full bg-white story-progress-fill" style={{ animationDuration: `${DUR}ms` }} />}
                </div>
              ))}
            </div>
            {cur.cover_image && <img src={resolveImg(cur.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="relative z-[5] p-5 h-full flex flex-col text-white pt-14">
              {curCat && <div className="mb-3"><CategoryChip category={curCat} size="lg" /></div>}
              <h3 className="font-display font-extrabold text-2xl leading-tight">{cur.title}</h3>
              <ul className="mt-5 space-y-3 text-sm">
                {cur.gist.slice(0,3).map((g,i) => <li key={i} className="flex gap-2"><span className="text-signal">●</span><span>{g}</span></li>)}
              </ul>
              <div className="mt-auto pt-6 flex gap-2">
                <button onClick={(e) => { e.stopPropagation(); nav(`/story/${cur.slug}`); close(); }}
                  className="flex-1 h-11 rounded-full bg-signal text-white font-bold text-sm" data-testid="sixty-read-full">Read full story →</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
